import max from 'lodash/max';
import copy from 'clipboard-copy';
import pick from 'lodash/pick';

import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {reporter} from 'common/report/error-reporting';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {Section} from 'common/components/items-menu/types';
import {DashboardSelectorController} from 'common/report/controllers/dashboard-selector-controller';
import {getBoardsDashboardSectionItem, getProjectSelectorSectionItem} from 'common/report/util/boards/presets';
import {SidenavItemPosition, SidenavSectionItem} from 'common/components/sidenav/types';
import {
  getSidenavItemWithInsertion,
  getSidenavItemWithoutValue,
  sidenavItemSplice,
} from 'common/components/sidenav/util';
import {Dashboard, DashboardType} from 'common/report/dashboards/types';
import {FeatureGateTier} from 'common/report/constants';
import {dashboardsClient} from 'common/report/queries/app-api/dashboards-client';
import {BoardMoveSetting, CurrentProject} from 'common/report/queries/app-api/project-metadata-client';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {ApiResponseError} from 'common/util/shared-client';
import {getProjectId, getReportUrl, getWorkspaceId} from 'common/report/util';
import {Content} from 'common/types/app-api/content';
import {contentUtils} from 'common/report/util/contents/utils';
import {SharableEntityType} from 'common/report/util/saved-entities/types';
import {
  ContentSidenavOrdering,
  getContentSidenav,
  updateContentSidenavOrdering,
} from 'common/report/queries/app-api/content-sidenav-client';
import {SvgIconType} from 'common/components/svg-icon/types';
import {LocalizationStore} from 'common/i18n/store';

import {ContentItemInfo, ContentSidenavSection, ContentSidenavSectionItem, DashboardItemInfo} from './types';
import {
  CONTENT_ITEM_ATTRIBUTES,
  DAYS_FOR_IS_NEW_TAG,
  SidenavOverflowAction,
  SidenavSectionIndex,
  SidenavSectionTrackingName,
} from './constants';

export async function copySidenavLink(href: string | undefined, i18n: LocalizationStore) {
  if (!href) {
    addToast({message: i18n.t(`sidenav.url-copy-none`), type: ToastType.ERROR});
    return;
  }
  try {
    await copy(new URL(href, document.baseURI).href);
    addToast({message: i18n.t(`sidenav.url-copy-success`), type: ToastType.SUCCESS});
  } catch (err) {
    reporter.error(err, `Failed to copy URL.`);
    addToast({message: i18n.t(`sidenav.url-copy-fail`), type: ToastType.ERROR});
  }
}

/**
 *
 * @param stringDate string date with format "YYYY-MM-DD"
 */
function daysSince(stringDate: string) {
  const diffInMillis = new Date().getTime() - new Date(stringDate).getTime();
  return diffInMillis / (1000 * 3600 * 24);
}

export const contentItemInfoToDashboardItemInfo = (content: ContentItemInfo): DashboardItemInfo => {
  return {
    id: parseInt(content.id, 10),
    title: content.name,
    description: content.description ?? ``,
    can_view: content.can_view ?? false,
    can_share: content.can_share ?? false,
    can_update_basic: content.can_update_basic ?? false,
    can_pin: content.can_pin ?? false,
    is_shared_with_project: content.is_shared_with_project ?? false,
    is_superadmin: content.is_superadmin ?? false,
    allow_staff_override: content.allow_staff_override ?? false,
    creator_email: content.creator_email ?? ``,
    creator_id: content.creator_id ?? 0,
    creator_name: content.creator_name ?? ``,
    pinned_date: content.pinned_date ?? undefined,
    is_favorited: content.is_favorited ?? false,
    child_dashboards: content.child_dashboards,
    parent_dashboard_id: content.parent_dashboard_id,
    generation_type: content.generation_type ?? DashboardType.UserCreated,
  };
};

export const dashboardItemInfoToContentItemInfo = (dashboard: DashboardItemInfo): ContentItemInfo => {
  return {
    id: dashboard.id.toString(),
    content_type: SharableEntityType.Dashboard,
    name: dashboard.title,
    description: dashboard.description,
    can_pin: dashboard.can_pin,
    creator_email: dashboard.creator_email || ``,
    creator_id: dashboard.creator_id || 0,
    creator_name: dashboard.creator_name || ``,
    pinned_date: dashboard.pinned_date,
    is_favorited: dashboard.is_favorited,
    is_shared_with_project: dashboard.is_shared_with_project,
    is_superadmin: dashboard.is_superadmin,
    allow_staff_override: dashboard.allow_staff_override,
    can_view: dashboard.can_view,
    can_share: dashboard.can_share,
    can_update_basic: dashboard.can_update_basic,
    child_dashboards: dashboard.child_dashboards,
    parent_dashboard_id: dashboard.parent_dashboard_id,
    generation_type: dashboard.generation_type,
  };
};

/** Get the height of the content hierarchy tree that has item as the root */
export function getContentHierarchyTreeHeight(item: ContentItemInfo): number {
  if (!item.child_dashboards || item.child_dashboards.length === 0) {
    return 1;
  }
  return (
    1 +
    (max<number>(
      item.child_dashboards.map((childDashboard) =>
        getContentHierarchyTreeHeight(dashboardItemInfoToContentItemInfo(childDashboard)),
      ),
    ) ?? 0)
  );
}

/** Get the set of content ids that are descendants of the given content */
export function getContentDescendants(item: ContentItemInfo): Set<string> {
  const resultSet = new Set([item.id]);

  if (!item.child_dashboards) {
    return resultSet;
  }

  const childrenDescendants = item.child_dashboards.flatMap((childDashboard) =>
    getContentDescendants(dashboardItemInfoToContentItemInfo(childDashboard)),
  );

  childrenDescendants.forEach((childDescendant) => {
    childDescendant.forEach((entry) => {
      resultSet.add(entry);
    });
  });

  return resultSet;
}

export function contentToSidenavSectionItem({
  content,
  sectionIndex,
  trackingName,
  isNew,
  selectorController,
  isEnterpriseFeatureGateTier,
  isNestedDashboardsEnabled,
  isCrossProjectBoardMovesEnabled,
  hideDashboardList,
  favoriteIds,
  pinnedIds,
  isDashboardsOnly,
}: {
  content: DashboardItemInfo | ContentItemInfo;
  sectionIndex: SidenavSectionIndex;
  trackingName: SidenavSectionTrackingName;
  isNew?: boolean;
  selectorController?: DashboardSelectorController;
  isEnterpriseFeatureGateTier?: boolean;
  isNestedDashboardsEnabled?: boolean;
  isCrossProjectBoardMovesEnabled?: boolean;
  hideDashboardList?: boolean;
  favoriteIds?: Array<number | string>;
  pinnedIds?: Array<number | string>;
  isDashboardsOnly?: boolean;
}): ContentSidenavSectionItem {
  if (!(`content_type` in content)) {
    return contentToSidenavSectionItem({
      content: dashboardItemInfoToContentItemInfo(content as DashboardItemInfo),
      sectionIndex,
      trackingName,
      isNew,
      selectorController,
      isEnterpriseFeatureGateTier,
      isNestedDashboardsEnabled,
      isCrossProjectBoardMovesEnabled,
      hideDashboardList,
      favoriteIds,
      pinnedIds,
      isDashboardsOnly,
    });
  }
  content = content as ContentItemInfo;
  const isDashboard = content.content_type === SharableEntityType.Dashboard;
  const isFavorite = content.is_favorited || Boolean(favoriteIds && favoriteIds.includes(content.id));
  const isPinned = Boolean(content.pinned_date) || Boolean(pinnedIds && pinnedIds.includes(content.id));

  const canEdit = contentUtils.canEditContent(content);
  const canShare = content.can_share;
  const canPin = content.can_pin;
  const hasParentContent = Boolean(content.parent_dashboard_id);
  const excludeContent = [content.id, ...(content.parent_dashboard_id ? [content.parent_dashboard_id] : [])];
  const isDraftTemplate = content?.generation_type === DashboardType.StagedTemplate;

  let moveItem;

  if (canEdit && !isDraftTemplate) {
    const sharedMoveToParams: any = {
      label: ({t}: LocalizationStore) => t(`sidenav.move`),
      icon: `share`,
      name: SidenavOverflowAction.Move,
    };
    if (!isDashboard) {
      // no move item
    } else if (!isNestedDashboardsEnabled && isCrossProjectBoardMovesEnabled) {
      moveItem = getProjectSelectorSectionItem({
        ...sharedMoveToParams,
        placeholder: ({t}: LocalizationStore) => t(`boards.actions.project-placeholder`),
        quickCreateSaveDashboardCopy: ({t}: LocalizationStore) => t(`boards.actions.create-new-project`),
      });
    } else if (isNestedDashboardsEnabled) {
      moveItem = getBoardsDashboardSectionItem({
        ...sharedMoveToParams,
        placeholder: ({t}: LocalizationStore) => t(`boards.actions.board-placeholder`),
        quickCreateSaveDashboardCopy: ({t}: LocalizationStore) => t(`boards.actions.create-new-board`),
        dashboardController: selectorController,
        recentDashboardIds: [],
        showProjectSelector: isCrossProjectBoardMovesEnabled,
        showMoveOutButton: isNestedDashboardsEnabled && hasParentContent,
        hideDashboardList,
        excludedDashboards: excludeContent,
        currentDashboardHierarchyTreeHeight: getContentHierarchyTreeHeight(content),
        descendantDashboardIds: getContentDescendants(content),
        enableNestedDashboardsRestrictions: true,
      });
    }
  }

  const overflowMenuSections: Section[] = [
    {
      items: [
        ...(canEdit
          ? [
              {
                label: ({t}: LocalizationStore) => t(`common.delete`),
                value: SidenavOverflowAction.Delete,
                icon: `trashcan`,
              },
              {
                label: ({t}: LocalizationStore) => t(`reports.actions.rename`),
                value: SidenavOverflowAction.Edit,
                icon: `edit`,
              },
            ]
          : []),
        {
          label: isFavorite ? ({t}: LocalizationStore) => t(`sidenav.unfavorite`) : ({t}) => t(`sidenav.favorite`),
          value: SidenavOverflowAction.Favorite,
          icon: isFavorite ? `filled-heart` : `heart`,
          highlightIcon: isFavorite,
        },
      ],
    },
    {
      items: [
        {
          label: ({t}: LocalizationStore) => t(`reports.actions.copy-url`),
          value: SidenavOverflowAction.Link,
          icon: `link`,
        },
        ...(!isDraftTemplate
          ? [
              {
                label: ({t}: LocalizationStore) => t(`reports.actions.duplicate`),
                value: SidenavOverflowAction.Duplicate,
                icon: `duplicate`,
              },
            ]
          : []),
        ...(moveItem ? [moveItem] : []),
      ],
    },
    {
      items: [
        ...(canPin
          ? [
              {
                label: isPinned
                  ? ({t}: LocalizationStore) => t(`sidenav.unpin`)
                  : ({t}: LocalizationStore) => t(`sidenav.pin`),
                value: SidenavOverflowAction.Pin,
                icon: isPinned ? `filled-pin` : `pin`,
                highlightIcon: isPinned,
              },
            ]
          : []),
        ...(canShare
          ? [
              {
                label: ({t}: LocalizationStore) => t(`sidenav.manage-sharing`),
                value: SidenavOverflowAction.Share,
                icon: `invite`,
              },
            ]
          : []),
      ],
    },
  ];
  return {
    content: contentToContentItemInfo(content),
    icon: isDashboardsOnly ? undefined : contentIcon(content),
    href: contentUrl(content),
    label: content.name,
    overflowMenuSections,
    isFavorite,
    sectionIndex,
    trackingName,
    userEmail: content.creator_email,
    userId: content.creator_id,
    userName: content.creator_name,
    value: content.id,
    isNew,
    nestDisabled: !isDashboard,
    nestedItems:
      content.child_dashboards?.map((childDashboard) =>
        contentToSidenavSectionItem({
          content: childDashboard,
          sectionIndex,
          trackingName,
          isNew,
          selectorController,
          isEnterpriseFeatureGateTier,
          isNestedDashboardsEnabled,
          isCrossProjectBoardMovesEnabled,
          hideDashboardList,
          favoriteIds,
          pinnedIds,
          isDashboardsOnly,
        }),
      ) || [],
  };
}

export function contentToContentItemInfo(content: Content): ContentItemInfo {
  return pick(content, ...CONTENT_ITEM_ATTRIBUTES);
}

export function contentUrl(content: ContentItemInfo): string {
  switch (content.content_type) {
    case SharableEntityType.Experiment:
      return getReportUrl(`experiments/${content.id}`, {
        projectId: getProjectId(),
        workspaceId: getWorkspaceId(),
      });
    case SharableEntityType.FeatureFlag:
      return getReportUrl(`feature-flags/${content.id}`, {
        projectId: getProjectId(),
        workspaceId: getWorkspaceId(),
      });
    case SharableEntityType.MetricTree:
      return getReportUrl(`metric-tree/${content.id}`, {
        projectId: getProjectId(),
        workspaceId: getWorkspaceId(),
      });
    case SharableEntityType.Dashboard:
      return getReportUrl(`boards#id=${content.id}`, {
        projectId: getProjectId(),
        workspaceId: getWorkspaceId(),
      });
    default:
      return ``;
  }
}

export function contentIcon(content: ContentItemInfo): SvgIconType | undefined {
  if (content?.child_dashboards?.length) {
    return undefined;
  }
  switch (content.content_type) {
    case SharableEntityType.Experiment:
      return `experiment`;
    case SharableEntityType.FeatureFlag:
      return `feature-flag`;
    case SharableEntityType.MetricTree:
      return `brand-canvas-solid`;
    case SharableEntityType.Dashboard:
      return `brand-dashboard-solid`;
    default:
      return undefined;
  }
}

export async function getSidenavSections({
  userId,
  projectId,
  seenPinnedContentIdsStorage,
  hideDashboardList,
  dashboardSelectorController,
  isEnterpriseFeatureGateTier,
  isNestedDashboardsEnabled,
  isCrossProjectBoardMovesEnabled,
  isDashboardsOnly,
}: {
  userId: number;
  projectId: number;
  seenPinnedContentIdsStorage: UserLocalStorageHelper<string[]>;
  hideDashboardList?: boolean;
  dashboardSelectorController?: DashboardSelectorController;
  isEnterpriseFeatureGateTier?: boolean;
  isNestedDashboardsEnabled?: boolean;
  isCrossProjectBoardMovesEnabled?: boolean;
  isDashboardsOnly?: boolean;
}): Promise<ContentSidenavSection[]> {
  /**
   * Favorites, pinned and created have `pinned_date` set. Recents are added at the end of this function
   */
  const response = await getContentSidenav(projectId);
  const {favorites, pinned, created} = response;
  const favoriteIds = favorites.map(({id}) => id);
  const pinnedIds = pinned.map(({id}) => id);
  const pinnedDates = Object.fromEntries(
    favorites
      .filter((content) => content.pinned_date)
      .concat(pinned)
      .map((content) => [content.id, content.pinned_date]),
  );
  const seenPinnedContentIds = seenPinnedContentIdsStorage.getItem({key: projectId, userId}) || [];
  const unseenPinnedIds = pinned
    .filter(
      (content) =>
        content.pinned_date &&
        seenPinnedContentIds.includes(content.id.toString()) &&
        daysSince(content.pinned_date) <= DAYS_FOR_IS_NEW_TAG,
    )
    .map(({id}) => id);

  const GUID_LENGTH = 36;

  seenPinnedContentIdsStorage.setItem({
    userId,
    key: projectId,
    value: Object.keys(pinnedDates).filter((id) => id.length >= GUID_LENGTH),
  });

  return [
    {
      key: `Favorites`,
      label: ({t}: LocalizationStore) => t(`sidenav.favorites`).toUpperCase(),
      dragDropEnabled: true,
      trackingName: SidenavSectionTrackingName.Favorites,
      items: favorites.map((content) =>
        contentToSidenavSectionItem({
          content: {...content, is_favorited: true},
          sectionIndex: SidenavSectionIndex.Favorites,
          trackingName: SidenavSectionTrackingName.Favorites,
          isNew: false,
          selectorController: dashboardSelectorController,
          isEnterpriseFeatureGateTier,
          isNestedDashboardsEnabled,
          isCrossProjectBoardMovesEnabled,
          hideDashboardList,
          favoriteIds,
          pinnedIds,
          isDashboardsOnly,
        }),
      ),
      isCollapsible: true,
    },
    {
      key: `Pinned`,
      label: ({t}: LocalizationStore) => t(`sidenav.pinned`).toUpperCase(),
      dragDropEnabled: true,
      trackingName: SidenavSectionTrackingName.Pinned,
      items: pinned.map((content) =>
        contentToSidenavSectionItem({
          content: {
            ...content,
            is_favorited: favoriteIds.includes(content.id),
          },
          sectionIndex: SidenavSectionIndex.Pinned,
          trackingName: SidenavSectionTrackingName.Pinned,
          isNew: unseenPinnedIds.includes(content.id),
          selectorController: dashboardSelectorController,
          isEnterpriseFeatureGateTier,
          isNestedDashboardsEnabled,
          isCrossProjectBoardMovesEnabled,
          hideDashboardList,
          favoriteIds,
          pinnedIds,
          isDashboardsOnly,
        }),
      ),
      isCollapsible: true,
    },
    {
      key: `Your Content`,
      label: ({t}: LocalizationStore) =>
        isDashboardsOnly ? t(`sidenav.your-boards`).toUpperCase() : t(`sidenav.your-content`).toUpperCase(),
      dragDropEnabled: true,
      trackingName: SidenavSectionTrackingName.Yours,
      items: created
        .filter((content) => !(`parent_dashboard_id` in content) || !(content as Dashboard).parent_dashboard_id)
        .map((content) =>
          contentToSidenavSectionItem({
            content: {
              ...content,
              is_favorited: favoriteIds.includes(content.id),
            },
            sectionIndex: SidenavSectionIndex.Yours,
            trackingName: SidenavSectionTrackingName.Yours,
            isNew: false,
            selectorController: dashboardSelectorController,
            isEnterpriseFeatureGateTier,
            isNestedDashboardsEnabled,
            isCrossProjectBoardMovesEnabled,
            hideDashboardList,
            favoriteIds,
            pinnedIds,
            isDashboardsOnly,
          }),
        ),
      isCollapsible: true,
    },
  ];
}

export async function updateSidenavOrdering(sections: ContentSidenavSection[], projectId: number) {
  const ordering: ContentSidenavOrdering = {
    favorites_order:
      findSectionByTrackingName(sections, SidenavSectionTrackingName.Favorites)?.items.map((item) => item.value) ?? [],
    pinned_order:
      findSectionByTrackingName(sections, SidenavSectionTrackingName.Pinned)?.items.map((item) => item.value) ?? [],
    created_by_user_order:
      findSectionByTrackingName(sections, SidenavSectionTrackingName.Yours)?.items.map((item) => item.value) ?? [],
  };

  return updateContentSidenavOrdering(ordering, projectId);
}

export function findSectionByTrackingName(sections: ContentSidenavSection[], trackingName: SidenavSectionTrackingName) {
  return sections.find((section) => section.trackingName === trackingName);
}

/**
 * Move item to a different section or position
 * Both starting and destination positions are top level
 */
export function moveSidenavItemAtTopLevel(
  sections: ContentSidenavSection[],
  sectionIndex: number,
  itemIndex: number,
  destinationSectionIndex: number,
  destinationItemIndex: number,
) {
  const item = {...sections[sectionIndex].items[itemIndex]};
  return sidenavItemSplice(
    sidenavItemSplice(sections, sectionIndex, itemIndex, 1),
    destinationSectionIndex,
    destinationItemIndex,
    0,
    item,
  );
}

/**
 * Move item to a different section or position
 * Starting position is nested and destination position is top level
 */
function moveSidenavItemFromParentToTopLevel(
  sections: ContentSidenavSection[],
  item: SidenavSectionItem,
  destinationPosition: SidenavItemPosition,
  userId: number,
) {
  for (const [sectionIndex, sectionFound] of sections.entries()) {
    for (const [itemIndex, itemFound] of sectionFound.items.entries()) {
      if (isValueInDashboardItem(itemFound, item.value)) {
        const {updatedItem} = getSidenavItemWithoutValue(item.value, itemFound);

        if (updatedItem || sectionIndex === destinationPosition.sectionIndex) {
          sections = sidenavItemSplice(sections, sectionIndex, itemIndex, 1, updatedItem);
        }
      }
    }
  }

  sections = sections.map((section) => {
    return {
      ...section,
      items: section.items.filter((currItem) => currItem),
    };
  });

  if (item.sectionIndex !== SidenavSectionIndex.Yours) {
    sections = sidenavItemSplice(sections, destinationPosition.sectionIndex!, destinationPosition.itemIndex!, 0, item);
    if (
      (item.dashboard || item.content)?.creator_id === userId &&
      sections[SidenavSectionIndex.Yours].items.every((existingItem) => existingItem.value !== item.value)
    ) {
      sections = sidenavItemSplice(
        sections,
        SidenavSectionIndex.Yours,
        sections[SidenavSectionIndex.Yours].items.length,
        0,
        item,
      );
    }
  } else if (item.dashboard?.creator_id === userId) {
    sections = sidenavItemSplice(sections, destinationPosition.sectionIndex!, destinationPosition.itemIndex!, 0, item);
  }

  return sections;
}

/**
 * Move item to a different section or position
 * Starting position is top level and destination position is nested
 */
function moveSidenavItemFromTopLevelToParent(
  sections: ContentSidenavSection[],
  item: SidenavSectionItem,
  currentPosition: SidenavItemPosition,
  destinationPosition: SidenavItemPosition,
) {
  for (const [sectionIndex, sectionFound] of sections.entries()) {
    for (const [itemIndex, itemFound] of sectionFound.items.entries()) {
      if (isValueInDashboardItem(itemFound, destinationPosition.parentItem!.value)) {
        const updatedItem = getSidenavItemWithInsertion(itemFound, item, destinationPosition.parentItem!.value);

        sections = sidenavItemSplice(sections, sectionIndex, itemIndex, 1, updatedItem);
      }
    }
  }

  if (item.sectionIndex === SidenavSectionIndex.Yours) {
    sections = sidenavItemSplice(sections, SidenavSectionIndex.Yours, currentPosition.itemIndex, 1);
  } else if (
    sections[SidenavSectionIndex.Yours].items.some(
      (existingItem) => existingItem.value !== item.value && isValueInDashboardItem(existingItem, item.value),
    )
  ) {
    for (const [itemIndex, itemFound] of sections[SidenavSectionIndex.Yours].items.entries()) {
      if (itemFound.value === item.value) {
        sections = sidenavItemSplice(sections, SidenavSectionIndex.Yours, itemIndex, 1);
      }
    }
  }

  return sections;
}

export function isValueInDashboardItem(item: SidenavSectionItem, itemValue: string): boolean {
  if (item.value === itemValue) {
    return true;
  }

  return item.nestedItems?.some((childItem) => isValueInDashboardItem(childItem, itemValue)) || false;
}

/**
 * Move item to a different section or position
 * Starting position is nested and destination position is nested
 */
function moveSidenavItemFromParentToParent(
  sections: ContentSidenavSection[],
  item: SidenavSectionItem,
  destinationPosition: SidenavItemPosition,
) {
  for (const [sectionIndex, sectionFound] of sections.entries()) {
    for (const [itemIndex, itemFound] of sectionFound.items.entries()) {
      if (isValueInDashboardItem(itemFound, item.value)) {
        const {updatedItem} = getSidenavItemWithoutValue(item.value, itemFound);

        if (updatedItem || sectionIndex === SidenavSectionIndex.Yours) {
          sections = sidenavItemSplice(sections, sectionIndex, itemIndex, 1, updatedItem);
        }
      }
    }
  }

  sections = sections.map((section) => {
    return {
      ...section,
      items: section.items.filter((currItem) => currItem),
    };
  });

  for (const [sectionIndex, sectionFound] of sections.entries()) {
    for (const [itemIndex, itemFound] of sectionFound.items.entries()) {
      if (isValueInDashboardItem(itemFound, destinationPosition.parentItem!.value)) {
        const updatedItem = getSidenavItemWithInsertion(itemFound, item, destinationPosition.parentItem!.value);

        sections = sidenavItemSplice(sections, sectionIndex, itemIndex, 1, updatedItem);
      }
    }
  }

  return sections;
}

/**
 * Move item with value itemValue to a different section or position
 */
export function getSectionsAfterMovingSidenavItem(
  sections: ContentSidenavSection[],
  item: ContentSidenavSectionItem,
  currentPosition: SidenavItemPosition,
  destinationPosition: SidenavItemPosition,
  userId: number,
) {
  // Moving at top level case
  if (!getSidenavItemParentValue(item) && !destinationPosition.parentItem) {
    return moveSidenavItemAtTopLevel(
      sections,
      currentPosition.sectionIndex!,
      currentPosition.itemIndex!,
      destinationPosition.sectionIndex!,
      destinationPosition.itemIndex!,
    );
  }

  // Un-nesting case
  if (getSidenavItemParentValue(item) && !destinationPosition.parentItem) {
    return moveSidenavItemFromParentToTopLevel(sections, item, destinationPosition, userId);
  }

  // Nesting from top level case
  if (!getSidenavItemParentValue(item) && destinationPosition.parentItem) {
    return moveSidenavItemFromTopLevelToParent(sections, item, currentPosition, destinationPosition);
  }

  // Nesting from another parent case
  if (getSidenavItemParentValue(item) && destinationPosition.parentItem) {
    return moveSidenavItemFromParentToParent(sections, item, destinationPosition);
  }

  return sections;
}

export function getSidenavItemParentValue(item: ContentSidenavSectionItem): string | null {
  return item?.content?.parent_dashboard_id ? item.content.parent_dashboard_id.toString() : null;
}

export function findFirstSidenavItemWithValue(
  sections: ContentSidenavSection[],
  value: string,
): ContentSidenavSectionItem | undefined {
  for (const [sectionIdx, section] of sections.entries()) {
    for (const [itemIdx, item] of section.items.entries()) {
      if (item.value === value) {
        return {...item, sectionIdx, itemIdx};
      }
    }
  }

  return undefined;
}

export const enum SidenavHeaderAction {
  Browse = `browse`,
  Home = `home`,
  New = `new`,
}

export const MOVED_BOARD_STORAGE_KEY = `last_move`;

export const reportErrorOnMovingBoard = (err: Error) => {
  let message = `Something went wrong moving a board. We've kept it safely in its old location.`;

  if (err instanceof ApiResponseError) {
    message = err.message;
  }

  reporter.error(err, `Error moving board between projects.`);
  addToast({
    message,
    type: ToastType.ERROR,
    timeout: 20000,
  });
};

export const getUserId = () => {
  return userCookieUtils.getUserIdentityFromCookie()?.id;
};

export const isOrgOnEnterprisePlan = (currentProject: CurrentProject) => {
  return currentProject?.featureGateTier === FeatureGateTier.Enterprise;
};

export const isCrossProjectBoardMovesEnabled = (currentProject: CurrentProject) => {
  return currentProject?.crossProjectBoardMovingSetting !== BoardMoveSetting.NOT_ALLOWED;
};

export const moveBoardViaDashboardClient = (
  dashboard: DashboardItemInfo,
  workspaceId: string,
  projectId: string,
  parentDashboardId?: number,
) => {
  if (parentDashboardId) {
    return dashboardsClient.nestDashboard({
      projectId,
      workspaceId,
      parentDashboardId,
      dashboard,
    });
  }
  return dashboardsClient.unnestDashboard({
    projectId,
    workspaceId,
    parentDashboardId: dashboard.parent_dashboard_id,
    dashboard,
  });
};
